import React, { useState } from 'react';
import { saveSwiperMappings } from '../services/plService';
import { unitOptions } from '../utils/unitNames';

// Shown after a CryptoPay upload when the CSV contained SwiperIDs that
// weren't in the location's per-swiper mapping and didn't match the
// built-in regex. The user picks a canonical unit for each, we save the
// mappings, and the backend retroactively re-maps historical transactions.
const UnmappedSwipersModal = ({ unmapped, location, onCancel, onSaved }) => {
  const options = unitOptions(location);
  // seed each row with the first option so submit is never blank
  const [choices, setChoices] = useState(() => {
    const seed = {};
    for (const u of unmapped) seed[u.swiper_id] = options[0]?.value || 'Other';
    return seed;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const setChoice = (swiperId, value) => setChoices((prev) => ({ ...prev, [swiperId]: value }));

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      const mappings = unmapped.map((u) => ({
        swiper_id: u.swiper_id,
        unit_name: choices[u.swiper_id],
        active: true,
      }));
      const result = await saveSwiperMappings(mappings, { remap: true });
      onSaved(result);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save mappings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Unrecognized swipers</h2>
          <p className="text-xs text-gray-500 mt-1">
            These SwiperIDs from the CryptoPay file don't match any equipment yet. Pick
            what each one is so their transactions get grouped correctly — now and going
            forward. Historical transactions with these swipers will also be re-mapped.
          </p>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{error}</div>}
          {unmapped.map((u) => (
            <div key={u.swiper_id} className="grid grid-cols-2 gap-3 items-center">
              <div className="min-w-0">
                <div className="text-sm font-mono text-gray-900 truncate">{u.swiper_id}</div>
                <div className="text-xs text-gray-500">{u.count} transaction{u.count === 1 ? '' : 's'} in this upload</div>
              </div>
              <select
                value={choices[u.swiper_id] || ''}
                onChange={(e) => setChoice(u.swiper_id, e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {options.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          ))}
        </div>

        <div className="px-6 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded text-sm font-medium"
          >
            Skip for now
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded text-sm font-medium"
          >
            {saving ? 'Saving…' : `Save mappings & re-map`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnmappedSwipersModal;
